import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _Asterisk, _InputLoadingIndicato;
var _excluded = ["checked", "destructive", "disabled", "helpIcon", "hintCheckbox", "hintCheckboxProps", "hintText", "iconLeading", "iconLeadingIcon", "iconTrailing", "iconTrailingIcon", "inputTooltipDescription", "inputTooltipLabel", "inputTooltipPlacement", "inputTooltipVariant", "inputWidth", "label", "labelLeading", "labelTooltipDescription", "labelTooltipLabel", "labelTooltipPlacement", "labelTooltipVariant", "labelTrailing", "leadingDropdown", "leadingDropdownLabel", "leadingDropdownMenuOptions", "leadingDropdownSelectedOption", "loading", "onChangeCheckbox", "readOnly", "required", "size"];
import React, { useRef } from 'react';
import MuiTextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Tooltip } from '../Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { Icon } from '../Icon';
import { Asterisk } from '../common/Astreisk';
import { DropdownMenu } from '../DropdownMenu';
import { CountryIcon } from '../CountryIcon';
import { InputLoadingIndicator } from '../common';
import { Checkbox } from '../Checkbox';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Component that allows users to enter text into the UI, used commonly in forms and dialogs.
 * Uses the MUI TextField component internally.
 * Mui documentation link - https://mui.com/material-ui/react-text-field/
 */
export function InputField(props) {
  var _props$checked = props.checked,
    checked = _props$checked === void 0 ? false : _props$checked,
    _props$destructive = props.destructive,
    destructive = _props$destructive === void 0 ? false : _props$destructive,
    _props$disabled = props.disabled,
    controlledDisabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$helpIcon = props.helpIcon,
    helpIcon = _props$helpIcon === void 0 ? false : _props$helpIcon,
    _props$hintCheckbox = props.hintCheckbox,
    hintCheckbox = _props$hintCheckbox === void 0 ? false : _props$hintCheckbox,
    hintCheckboxProps = props.hintCheckboxProps,
    hintText = props.hintText,
    _props$iconLeading = props.iconLeading,
    iconLeading = _props$iconLeading === void 0 ? false : _props$iconLeading,
    iconLeadingIcon = props.iconLeadingIcon,
    _props$iconTrailing = props.iconTrailing,
    iconTrailing = _props$iconTrailing === void 0 ? false : _props$iconTrailing,
    iconTrailingIcon = props.iconTrailingIcon,
    inputTooltipDescription = props.inputTooltipDescription,
    inputTooltipLabel = props.inputTooltipLabel,
    inputTooltipPlacement = props.inputTooltipPlacement,
    inputTooltipVariant = props.inputTooltipVariant,
    inputWidth = props.inputWidth,
    label = props.label,
    labelLeading = props.labelLeading,
    labelTooltipDescription = props.labelTooltipDescription,
    labelTooltipLabel = props.labelTooltipLabel,
    labelTooltipPlacement = props.labelTooltipPlacement,
    labelTooltipVariant = props.labelTooltipVariant,
    labelTrailing = props.labelTrailing,
    _props$leadingDropdow = props.leadingDropdown,
    leadingDropdown = _props$leadingDropdow === void 0 ? false : _props$leadingDropdow,
    _props$leadingDropdow2 = props.leadingDropdownLabel,
    leadingDropdownLabel = _props$leadingDropdow2 === void 0 ? true : _props$leadingDropdow2,
    leadingDropdownMenuOptions = props.leadingDropdownMenuOptions,
    leadingDropdownSelectedOption = props.leadingDropdownSelectedOption,
    loading = props.loading,
    onChangeCheckbox = props.onChangeCheckbox,
    _props$readOnly = props.readOnly,
    readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
    _props$required = props.required,
    required = _props$required === void 0 ? false : _props$required,
    _props$size = props.size,
    size = _props$size === void 0 ? 'md' : _props$size,
    otherProps = _objectWithoutProperties(props, _excluded);
  var theme = useTheme();
  var disabled = controlledDisabled || loading;
  var inputRef = useRef(null);
  var dropdownLabel = !leadingDropdownLabel && !iconLeading ? true : leadingDropdownLabel;
  return /*#__PURE__*/_jsxs(Stack, {
    spacing: 1.5,
    sx: _extends({
      width: inputWidth != null ? inputWidth : '100%'
    }, otherProps.sx),
    "data-element": "Stack",
    "data-component": "InputField",
    "data-source-file": "InputField.tsx",
    children: [label && /*#__PURE__*/_jsxs(Stack, {
      alignItems: 'center',
      direction: "row",
      spacing: 1,
      children: [/*#__PURE__*/_jsx(Typography, {
        color: theme.palette.gray[700],
        variant: "text-sm-medium",
        children: label
      }), required && (_Asterisk || (_Asterisk = /*#__PURE__*/_jsx(Asterisk, {}))), labelTooltipLabel && /*#__PURE__*/_jsx(Tooltip, {
        description: labelTooltipDescription,
        label: labelTooltipLabel,
        placement: labelTooltipPlacement,
        variant: labelTooltipVariant,
        children: /*#__PURE__*/_jsx(Icon, {
          iconId: "HelpCircle",
          size: 16,
          sx: {
            '&:hover': {
              color: theme.palette.gray[500]
            },
            color: theme.palette.gray[400]
          }
        })
      })]
    }), /*#__PURE__*/_jsx(MuiTextField, _extends({
      InputProps: {
        endAdornment: (loading || helpIcon || iconTrailingIcon || labelTrailing) && /*#__PURE__*/_jsx(InputAdornment, {
          position: "end",
          sx: {
            marginLeft: '0px'
          },
          children: loading ? _InputLoadingIndicato || (_InputLoadingIndicato = /*#__PURE__*/_jsx(InputLoadingIndicator, {})) : /*#__PURE__*/_jsxs(Stack, {
            alignItems: "center",
            direction: "row",
            spacing: 2,
            children: [labelTrailing && /*#__PURE__*/_jsx(Typography, {
              color: theme.palette.gray[500],
              sx: {
                margin: '0px 8px'
              },
              variant: "text-md-regular",
              children: labelTrailing
            }), iconTrailing && iconTrailingIcon && /*#__PURE__*/_jsx(Icon, {
              color: theme.palette.gray[500],
              iconId: iconTrailingIcon,
              size: 20
            }), helpIcon && /*#__PURE__*/_jsx(Tooltip, {
              description: inputTooltipDescription,
              label: inputTooltipLabel,
              placement: inputTooltipPlacement,
              variant: inputTooltipVariant,
              children: /*#__PURE__*/_jsx(Icon, {
                iconId: "HelpCircle",
                size: 16,
                sx: {
                  '&:hover': {
                    color: theme.palette.gray[500]
                  },
                  justifyContent: 'right'
                }
              })
            })]
          })
        }),
        readOnly: destructive || disabled ? false : readOnly,
        ref: inputRef,
        startAdornment: /*#__PURE__*/_jsx(InputAdornment, {
          position: "start",
          sx: {
            marginRight: !iconLeading && !labelLeading ? '0px' : '8px'
          },
          children: (iconLeading || leadingDropdown || labelLeading) && /*#__PURE__*/_jsxs(Stack, {
            alignItems: "center",
            direction: "row",
            spacing: 2,
            children: [leadingDropdown && leadingDropdownMenuOptions && leadingDropdownSelectedOption && /*#__PURE__*/_jsx(DropdownMenu, {
              inputRef: inputRef,
              menuItemProps: leadingDropdownMenuOptions,
              menuPaperProps: {
                sx: {
                  minWidth: '50px'
                }
              },
              menuTriggerProps: {
                buttonText: dropdownLabel && leadingDropdownSelectedOption.content,
                disabled: disabled,
                startIcon: iconLeading && leadingDropdownSelectedOption.countryIconId ? /*#__PURE__*/_jsx(CountryIcon, {
                  id: leadingDropdownSelectedOption.countryIconId,
                  size: 20
                }) : leadingDropdownSelectedOption == null ? void 0 : leadingDropdownSelectedOption.icon,
                sx: {
                  '&.MuiButton-root': {
                    '& .MuiButton-startIcon': {
                      marginRight: !leadingDropdownLabel ? '0px !important' : '8px'
                    },
                    cursor: readOnly ? 'default' : 'pointer',
                    outline: 'none !important',
                    padding: '2px !important',
                    pointerEvents: readOnly ? 'none' : 'unset'
                  }
                },
                triggerType: 'Button',
                variant: 'link-gray'
              }
            }), iconLeading && iconLeadingIcon && !leadingDropdown && /*#__PURE__*/_jsx(Icon, {
              color: theme.palette.gray[500],
              iconId: iconLeadingIcon,
              size: 20
            }), labelLeading && /*#__PURE__*/_jsx(Typography, {
              color: theme.palette.gray[500],
              sx: {
                margin: '0px 8px'
              },
              variant: "text-md-regular",
              children: labelLeading
            })]
          })
        })
      },
      disabled: disabled,
      error: destructive,
      helperText: !hintCheckbox && hintText,
      required: required,
      size: size,
      variant: "outlined"
    }, otherProps, {
      sx: {
        'input:-webkit-autofill': {
          transition: "background-color 5000s"
        },
        width: inputWidth != null ? inputWidth : '100%'
      },
      "data-element": "MuiTextField",
      "data-source-file": "InputField.tsx"
    })), hintCheckbox && /*#__PURE__*/_jsxs(Stack, {
      alignItems: 'flex-start',
      direction: "row",
      children: [/*#__PURE__*/_jsx(Checkbox, _extends({
        checked: checked,
        onChange: onChangeCheckbox
      }, hintCheckboxProps, {
        disabled: disabled,
        readOnly: readOnly,
        size: "sm",
        sx: {
          marginRight: '8px !important',
          marginTop: '2px !important'
        }
      })), /*#__PURE__*/_jsx(Typography, {
        color: destructive ? theme.palette.error[600] : theme.palette.gray[600],
        variant: "text-sm-regular",
        children: hintText
      })]
    })]
  });
}